import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { SoutenanceEvaluationForm, SoutenanceEvaluationHeader, SoutenanceEvaluationList } from './evaluations';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

const EvaluationsTab = ({ onDataChange }) => {
  // États de base
  const [evaluations, setEvaluations] = useState([]);
  const [soutenances, setSoutenances] = useState([]);
  const [criteres, setCriteres] = useState([]);
  const [enseignantsInternes, setEnseignantsInternes] = useState([]);
  const [enseignantsExternes, setEnseignantsExternes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    critere: '',
    evaluateur_type: '',
    soutenance: '',
    note_min: '',
    note_max: ''
  });

  // États pour les modals
  const [showModal, setShowModal] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // États pour les données sélectionnées
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [evaluationToDelete, setEvaluationToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // États pour le formulaire
  const [formData, setFormData] = useState({
    soutenance: '',
    evaluateur_interne: '',
    evaluateur_externe: '',
    critere: '',
    note: '',
    commentaire: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // État pour les statistiques
  const [stats, setStats] = useState(null);

  // Charger les données initiales
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage,
        page_size: pageSize,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.critere && { critere: filters.critere }),
        ...(filters.evaluateur_type && { evaluateur_type: filters.evaluateur_type }),
        ...(filters.soutenance && { soutenance: filters.soutenance }),
        ...(filters.note_min && { note_min: filters.note_min }),
        ...(filters.note_max && { note_max: filters.note_max })
      });

      const [evaluationsRes, soutenancesRes, ensIntRes, ensExtRes, statsRes] = await Promise.all([
        api.get('/soutenance/evaluations/', { params }),
        api.get('/soutenance/soutenances/'),
        api.get('/enseignant/enseignants/'),
        api.get('/soutenance/enseignants-externes/'),
        api.get('/soutenance/evaluations/stats/')
      ]);

      if (evaluationsRes.data.status === 'success') {
        setEvaluations(evaluationsRes.data.data || []);
        setPagination(evaluationsRes.data.pagination);
      }
      
      if (soutenancesRes.data.status === 'success') {
        setSoutenances(soutenancesRes.data.data || []);
      }
      
      if (ensIntRes.data.status === 'success') {
        setEnseignantsInternes(ensIntRes.data.data || []);
      }
      
      if (ensExtRes.data.status === 'success') {
        setEnseignantsExternes(ensExtRes.data.data || []);
      }

      if (statsRes.data.status === 'success') {
        setStats(statsRes.data.data);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors du chargement des données';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filters]);

  // Charger les critères pour une soutenance
  const loadCriteres = useCallback(async (soutenanceId) => {
    try {
      setCriteres([]); // Reset before loading
      const soutenance = soutenances.find(s => s.id === parseInt(soutenanceId));
      if (!soutenance) {
        console.warn('Soutenance non trouvée pour ID:', soutenanceId);
        return;
      }
      // Defensive: type_soutenance can be object or id
      let typeSoutenanceId = null;
      if (soutenance.type_soutenance) {
        if (typeof soutenance.type_soutenance === 'object' && soutenance.type_soutenance.id) {
          typeSoutenanceId = soutenance.type_soutenance.id;
        } else if (typeof soutenance.type_soutenance === 'number') {
          typeSoutenanceId = soutenance.type_soutenance;
        }
      }
      if (!typeSoutenanceId) {
        console.warn('type_soutenance manquant ou invalide pour la soutenance:', soutenance);
        // Fallback: charger tous les critères
        await loadAllCriteres();
        return;
      }
      const response = await api.get(`/soutenance/types-soutenance/${typeSoutenanceId}/criteres/`);
      if (response.data.status === 'success' && response.data.data && response.data.data.length > 0) {
        setCriteres(response.data.data);
      } else {
        console.log('Aucun critère spécifique trouvé, chargement de tous les critères');
        // Fallback: si aucun critère spécifique, charger tous les critères
        await loadAllCriteres();
      }
    } catch (error) {
      console.error('Erreur lors du chargement des critères spécifiques:', error);
      console.log('Tentative de chargement de tous les critères en fallback');
      // Fallback: en cas d'erreur, essayer de charger tous les critères
      try {
        await loadAllCriteres();
      } catch (fallbackError) {
        console.error('Erreur lors du chargement des critères en fallback:', fallbackError);
        toast.error('Erreur lors du chargement des critères d\'évaluation');
        setCriteres([]);
      }
    }
  }, [soutenances]);

  // Charger tous les critères disponibles
  const loadAllCriteres = useCallback(async () => {
    try {
      const response = await api.get('/soutenance/criteres/');
      if (response.data.status === 'success') {
        setCriteres(response.data.data || []);
      } else {
        setCriteres([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des critères:', error);
      toast.error('Erreur lors du chargement des critères d\'évaluation');
      setCriteres([]);
    }
  }, []);

  // Effet pour charger les critères quand la soutenance change
  useEffect(() => {
    setFormData(prev => ({ ...prev, critere: '' })); // Reset critere when soutenance changes
    if (formData.soutenance) {
      // Essayer d'abord de charger les critères spécifiques au type
      loadCriteres(formData.soutenance);
    } else {
      // Charger tous les critères disponibles si aucune soutenance sélectionnée
      loadAllCriteres();
    }
  }, [formData.soutenance, loadCriteres, loadAllCriteres]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Gestion des changements de page
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Gestion des changements de formulaire
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
    
    // Effacer les erreurs du champ modifié
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal pour créer/modifier
  const handleOpenModal = (evaluation = null) => {
    if (evaluation) {
      setSelectedEvaluation(evaluation);
      setFormData({
        soutenance: evaluation.soutenance?.id || '',
        evaluateur_interne: evaluation.evaluateur_interne?.id || '',
        evaluateur_externe: evaluation.evaluateur_externe?.id || '',
        critere: evaluation.critere?.id || '',
        note: evaluation.note,
        commentaire: evaluation.commentaire || ''
      });
    } else {
      setSelectedEvaluation(null);
      setFormData({
        soutenance: '',
        evaluateur_interne: '',
        evaluateur_externe: '',
        critere: '',
        note: '',
        commentaire: ''
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError('');

      const url = selectedEvaluation 
        ? `/soutenance/evaluations/${selectedEvaluation.id}/`
        : `/soutenance/soutenances/${formData.soutenance}/evaluations/`;
      
      const method = selectedEvaluation ? 'put' : 'post';
      
      const response = await api[method](url, formData);

      if (response.data.status === 'success') {
        toast.success(selectedEvaluation ? 'Évaluation modifiée avec succès' : 'Évaluation créée avec succès');
        await loadData();
        setShowModal(false);
        onDataChange && onDataChange();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Préparer la suppression
  const handlePrepareDelete = (evaluation) => {
    setEvaluationToDelete(evaluation);
    setConfirmConfig({
      title: 'Supprimer l\'évaluation',
      message: `Êtes-vous sûr de vouloir supprimer l'évaluation "${evaluation.critere?.nom}" ?`,
      confirmText: 'Supprimer',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  // Confirmer la suppression
  const handleDelete = async () => {
    if (!evaluationToDelete) return;
    
    try {
      setDeleteLoading(true);
      const response = await api.delete(`/soutenance/evaluations/${evaluationToDelete.id}/`);
      
      if (response.data.status === 'success') {
        toast.success('Évaluation supprimée avec succès');
        await loadData();
        onDataChange && onDataChange();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
      setEvaluationToDelete(null);
    }
  };

  // Rendu du header
  const renderHeader = () => (
    <SoutenanceEvaluationHeader
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      onAddNew={() => handleOpenModal()}
      filters={filters}
      onFilterChange={setFilters}
      soutenances={soutenances}
      criteres={criteres}
      stats={stats}
    />
  );

  // Rendu de la liste des évaluations
  const renderEvaluationsList = () => (
    <SoutenanceEvaluationList
      evaluations={evaluations}
      loading={loading}
      onEdit={handleOpenModal}
      onDelete={handlePrepareDelete}
    />
  );

  // Rendu du modal
  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      title={selectedEvaluation ? 'Modifier l\'Évaluation' : 'Nouvelle Évaluation'}
      maxWidth="max-w-2xl"
    >
      <SoutenanceEvaluationForm
        formData={formData}
        formErrors={formErrors}
        selectedEvaluation={selectedEvaluation}
        onInputChange={handleInputChange}
        onSubmit={handleSubmit}
        submitting={submitting}
        soutenances={soutenances}
        criteres={criteres}
        enseignantsInternes={enseignantsInternes}
        enseignantsExternes={enseignantsExternes}
      />
    </Modal>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {renderHeader()}

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Liste des évaluations */}
        {renderEvaluationsList()}

        {/* Pagination */}
        {pagination && evaluations.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* Modal */}
        {renderModal()}

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default EvaluationsTab;
